import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage, isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { MessageBubble } from '@/messages/MessageBubble';
import { MessageInput } from '@/messages/MessageInput';
import { MessageSendError } from '@/messages/MessageSendError';
import { MessagesSidebarLayout } from '@/messages/MessagesSidebarLayout';
import {
  avatarFor,
  formatMessageTimestamp,
  formatThreadDate,
  threadTitle,
} from '@/messages/messagesHelpers';
import {
  ChatHeader,
  ChatHeaderSubtitle,
  ChatHeaderText,
  ChatHeaderTitle,
  EmptyState,
  MenuButton,
  MenuContainer,
  MessagesContainer,
} from '@/messages/messagesStyles';
import { ThreadDropdownMenu } from '@/messages/ThreadDropdownMenu';
import { useSelfOnlyMessages } from '@/messages/useSelfOnlyMessages';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/messages/view/$threadId',
)({
  component: MessagesViewRoute,
});

function MessagesViewRoute() {
  const { t, i18n } = useTranslation();
  const { memberId, threadId: threadIdParam } = Route.useParams();
  const navigate = useNavigate();
  const threadId = Number.parseInt(threadIdParam, 10);
  const validThreadId = Number.isFinite(threadId);
  const wrongMember = useSelfOnlyMessages(memberId);

  const [showMenu, setShowMenu] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const {
    data: conversation,
    isPending,
    isError,
    error,
  } = api.getThreadMessages.useQuery(
    { path: { thread_id: threadId } },
    {
      enabled: validThreadId,
      // A thread that does not exist, or one the caller is not in, is a final
      // answer — retrying it three times only delays the message.
      retry: (failureCount: number, err: unknown) =>
        failureCount < 3 &&
        !isErrorCode(err, 'message_thread_not_found') &&
        !isErrorCode(err, 'not_thread_participant'),
    },
  );

  const { mutateAsync: sendMessage } = api.sendThreadMessage.useMutation();
  const { mutateAsync: markUnread } = api.markMessageThreadUnread.useMutation();
  const { mutateAsync: hideThread } = api.hideMessageThread.useMutation();
  const { mutateAsync: deleteThread } = api.deleteMessageThread.useMutation();

  const refreshConversation = async () => {
    await Promise.all([
      api.getThreadMessages.invalidateQueries(),
      api.listMessageThreads.invalidateQueries(),
      api.getUnreadMessageCount.invalidateQueries(),
    ]);
  };

  // Opening a conversation with incoming messages marks it read.
  //
  // Not while the URL names someone else: the redirect is on its way and
  // marking read is a real write, so a hand-typed wrong-member thread URL would
  // otherwise clear an unread count on a screen the viewer never sees.
  const { mutate: markRead } = api.markMessageThreadRead.useMutation();
  const hasIncomingMessages = !!conversation?.messages.some((m) => !m.is_you);
  useEffect(() => {
    if (validThreadId && !wrongMember && hasIncomingMessages) {
      markRead(
        { path: { thread_id: threadId } },
        {
          onSuccess: () => {
            api.listMessageThreads.invalidateQueries();
            api.getUnreadMessageCount.invalidateQueries();
          },
        },
      );
    }
  }, [threadId, validThreadId, wrongMember, hasIncomingMessages, markRead]);

  const handleSendMessage = async (message: string) => {
    if (!validThreadId) return;
    setFailure(null);

    let landedIn: number;
    try {
      const response = await sendMessage({
        path: { thread_id: threadId },
        body: { content: message },
      });
      landedIn = response.thread_id;
    } catch (error) {
      setFailure(error);
      // Rethrown so MessageInput keeps the draft — it clears only on resolve.
      throw error;
    }

    await refreshConversation();

    // A non-initiator's reply to a group thread goes into their 1:1 thread with
    // the sender, as in legacy. The response names the thread the message
    // landed in, so follow it; otherwise the reply is invisible. Not `replace`:
    // the group thread is a real previous location.
    if (landedIn !== threadId) {
      navigate({
        to: '/members/$memberId/messages/view/$threadId/',
        params: { memberId, threadId: `${landedIn}` },
      });
    }
  };

  const closeToInbox = async () => {
    setShowMenu(false);
    await api.listMessageThreads.invalidateQueries();
    navigate({ to: '/members/$memberId/messages/', params: { memberId } });
  };

  if (wrongMember) return null;

  return (
    <MessagesSidebarLayout
      activeThreadId={threadId}
      onThreadClick={(thread) =>
        navigate({
          to: '/members/$memberId/messages/view/$threadId/',
          params: { memberId, threadId: `${thread.id}` },
        })
      }
    >
      {!validThreadId || isError ? (
        <EmptyState>
          {errorMessage(
            error,
            isErrorCode(error, 'not_thread_participant')
              ? t('wbs.you-dont-have-permission')
              : t('wbs.something-went-wrong-please'),
          )}
        </EmptyState>
      ) : isPending || !conversation ? (
        <EmptyState>
          <LoadingSpinner size={24} centered />
        </EmptyState>
      ) : (
        <>
          <ChatHeader>
            <ChatHeaderText>
              {/* Plain text, never a profile link: a deleted participant
                 has no profile, and a group thread is titled after its
                 sender. */}
              <ChatHeaderTitle>
                {threadTitle(t, conversation.recipient, conversation.is_group)}
              </ChatHeaderTitle>
              {conversation.started_at && (
                <ChatHeaderSubtitle>
                  {t('wbs.started')}{' '}
                  {formatThreadDate(
                    conversation.started_at,
                    i18n.resolvedLanguage,
                  )}
                </ChatHeaderSubtitle>
              )}
            </ChatHeaderText>
            <MenuContainer>
              <MenuButton
                // Translation Needed
                aria-label="More options"
                onClick={() => setShowMenu(!showMenu)}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </MenuButton>
              {showMenu && (
                <ThreadDropdownMenu
                  onMarkUnread={async () => {
                    setShowMenu(false);
                    await markUnread({ path: { thread_id: threadId } });
                    await refreshConversation();
                  }}
                  onHideThread={async () => {
                    await hideThread({ path: { thread_id: threadId } });
                    await closeToInbox();
                  }}
                  onDeleteMessages={async () => {
                    await deleteThread({ path: { thread_id: threadId } });
                    await closeToInbox();
                  }}
                />
              )}
            </MenuContainer>
          </ChatHeader>

          <MessagesContainer>
            {conversation.messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={{
                  id: message.id,
                  sender: message.is_you
                    ? t('wbs.you')
                    : message.sender_is_deleted_user
                      ? t('actions.deleted-user')
                      : message.sender_name,
                  avatarUrl: avatarFor(
                    message.sender_name,
                    message.sender_avatar_url,
                  ),
                  content: message.content,
                  timestamp: formatMessageTimestamp(
                    message.created_at,
                    i18n.resolvedLanguage,
                  ),
                  isCurrentUser: message.is_you,
                }}
              />
            ))}
          </MessagesContainer>

          {failure !== null && <MessageSendError error={failure} />}

          {/* The group message notice, shown above the reply box as in
             legacy. The server sends null to the sender, so there's no
             client-side sender check. */}
          {conversation.mass_message_notice && (
            <MassMessageNoticeLine notice={conversation.mass_message_notice} />
          )}

          {/* No composer and no message when the send would be refused.
             Unlike legacy, which shows the reply form to every 1:1
             participant, this follows the send gate. */}
          {conversation.can_send && <MessageInput onSend={handleSendMessage} />}
        </>
      )}
    </MessagesSidebarLayout>
  );
}

/**
 * One of two sentences chosen on the group's type, as in legacy: the
 * congregation and custom study group version names and links the group; the
 * study helper group version names none.
 *
 * The keys live under `groups`, not `wbs`. A missing key renders as the key
 * itself, so `split('%s')` would silently drop the sentence's tail.
 */
function MassMessageNoticeLine({
  notice,
}: {
  notice: components['schemas']['MassMessageNotice'];
}) {
  const { t } = useTranslation();

  if (!notice.is_group_led) {
    return (
      <GroupMessageNotice>{t('groups.this-is-a-group-2')}</GroupMessageNotice>
    );
  }
  const [before, after] = t('groups.this-is-a-group').split('%s');
  return (
    <GroupMessageNotice>
      {before}
      <Link to="/groups/$groupName/" params={{ groupName: notice.group_slug }}>
        {notice.group_name}
      </Link>
      {after}
    </GroupMessageNotice>
  );
}

const GroupMessageNotice = styled.p`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  padding: 8px 16px 0;

  a {
    color: var(--wbs-red);
    text-decoration: underline;
  }
`;
